import { useState, useEffect } from 'react';
import { Formik, Form } from 'formik';
import { isEqual } from 'lodash';
import Yup from '../../utils/yup';

import addNotification from '../../utils/addNotification';
import { NotificationType } from '../../config';
import useUsers from '../../context/UsersContext';
import { FormButtonPanelUI, FormHeaderUI, DeleteDialogUI } from '../Interface';
import masterDataService from '../../service/masterData.service';
import { generateAutocompleteItems } from '../../utils/helper';
import ProjectTaskTabs from './ProjectTaskTabs';
import ProjectTaskFields from './ProjectTaskFields';
import taskListService from '../../service/taskList.service';
import dayjs from 'dayjs';
import History from '../History/History';
import HistoryDialog from '../History/HistoryDialog';
import { useParams } from 'react-router-dom';
import { getResponsibleList } from '../../utils/getResponsibleList';
import { TaskResponsibleTypeEnum } from '../../config/taskList';

export const ProjectTaskPage = ({ projectTasksState, dialog }) => {
  const {
    refetchTable,
    setRefetchTable,
    viewField,
    setViewField,
    selectedValues,
    setSelectedValues,
    operatorsDisabled,
    setOperatorsDisabled,
    submenu,
    canEdit,
    rowSelectionModel,
    setRowSelectionModel,
    autocompleteData,
    setAutocompleteData,
    autocompleteOptions,
    setAutoCompleteOptions,
    projectTaskSubmenu,
  } = projectTasksState;
  const { id } = useParams();
  const { getUser, user } = useUsers();
  const [editButtonVisible, setEditButtonvisible] = useState(true);
  const [addField, setAddField] = useState(true);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);

  const [defaultValues, setDefaultValues] = useState({});
  const [initValues, setInitValues] = useState({});
  const [defaultSelectedValues, setDefaultSelectedValues] = useState({});
  const [historyDialogOpen, setHistoryDialogOpen] = useState(false);

  const defTaskListRespPerson = {
    id: null,
    isNew: true,
    userId: null,
  };

  const def = {
    startDate: dayjs(),
    lineId: null,
    name: '',
    duration: 1,
    responsiblePersonId: null,
    responsibleUserGroupId: null,
    responsibleCompanyId: null,
    responsibleWorkerId: null,
    responsibleType: TaskResponsibleTypeEnum.USER,
    userGroupId: null,
    typeId: null,
    statusId: null,
    description: '',
    taskListAttachment: [],
    deadLine: null,
    projectOrderNumber: '',
    userId: user?.userId,
    preparedness: null,
    taskListRespPerson: [],
    taskRatio: '',
  };

  useEffect(() => {
    if (!Object.keys(user).length) getUser();
    setDefaultValues(def);
    setDefaultSelectedValues(def);
  }, []);

  useEffect(() => {
    setDefaultValues((prev) => {
      return { ...prev, userId: user.userId };
    });

    setDefaultSelectedValues((prev) => {
      return {
        ...prev,
        user: user,
      };
    });
  }, [user.userId]);

  useEffect(() => {
    if (dialog?.data?.id && Object.keys(autocompleteData).length) {
      taskListService.getTasklistById(dialog.data.id).then((data) => {
        setSelectedValues(data[0]);
        //setRowSelectionModel([data.id]);
      });
    }
  }, [dialog?.data, autocompleteData]);

  useEffect(() => {
    if (submenu) {
      initDefaultValues();
    }
  }, [submenu]);

  useEffect(() => {
    if (Object.keys(autocompleteData).length > 0) {
      setDefaultValues((prev) => {
        return {
          ...prev,
          statusId: autocompleteData?.status?.defaultValue ?? null,
          preparedness: autocompleteData?.status?.defItem?.preparedness ?? null,
          typeId: autocompleteData?.type?.defaultValue ?? null,
          ...(dialog?.data && !dialog?.data?.id && { ...dialog?.data }),
        };
      });

      setDefaultSelectedValues((prev) => {
        return {
          ...prev,
          status: autocompleteData?.status?.defItem,
          type: autocompleteData?.type?.defItem,
        };
      });
    }
  }, [autocompleteData]);

  useEffect(() => {
    if (Object.keys(selectedValues).length > 0) {
      if (!isEqual(defaultSelectedValues, selectedValues)) {
        valuesToForm();
        setOperatorsDisabled(false);
      } else {
        setOperatorsDisabled(true);
        setInitValues(defaultValues);
      }
    } else {
      setOperatorsDisabled(true);
      setInitValues(defaultValues);
    }
  }, [selectedValues]);

  useEffect(() => {
    if (id && Object.keys(autocompleteData).length > 0) {
      getRowById();
    } else {
      setSelectedValues(defaultSelectedValues);
    }
  }, [defaultSelectedValues, id, autocompleteData]);

  const getRowById = () => {
    taskListService.getTasklistById(id).then((data) => {
      setSelectedValues(data[0]);
      setRowSelectionModel(data[0].id);
    });
  };

  const initDefaultValues = () => {
    const promiseArray = [];

    promiseArray.push(
      new Promise((resolve, reject) => {
        masterDataService
          .projectToForm()
          .then((data) => {
            resolve(data);
          })
          .catch((e) => {
            reject(e);
          });
      })
    );

    promiseArray.push(
      new Promise((resolve, reject) => {
        masterDataService
          .usersToForm()
          .then((data) => {
            resolve(data);
          })
          .catch((e) => {
            reject(e);
          });
      })
    );

    promiseArray.push(
      new Promise((resolve, reject) => {
        masterDataService
          .userGroupsToForm()
          .then((data) => {
            resolve(data);
          })
          .catch((e) => {
            reject(e);
          });
      })
    );

    promiseArray.push(
      new Promise((resolve, reject) => {
        masterDataService
          .typeToForm(submenu)
          .then((data) => {
            resolve(data);
          })
          .catch((e) => {
            reject(e);
          });
      })
    );

    promiseArray.push(
      new Promise((resolve, reject) => {
        masterDataService
          .statusToForm(submenu)
          .then((data) => {
            resolve(data);
          })
          .catch((e) => {
            reject(e);
          });
      })
    );

    promiseArray.push(getResponsibleList());

    Promise.all(promiseArray)
      .then((resp) => {
        const aData = {
          project: resp[0],
          user: resp[1],
          userGroup: resp[2],
          type: resp[3],
          status: resp[4],
          responsible: resp[5],
        };
        setAutocompleteData(aData);

        const acOptions = {
          project: generateAutocompleteItems(aData.project, ['projectName', 'orderNumber'], 'projectId'),
          user: generateAutocompleteItems(aData.user, 'nickName', 'userId'),
          userGroup: generateAutocompleteItems(aData.userGroup, 'name', 'id'),
          type: generateAutocompleteItems(aData.type.typeItems, 'itemName', 'itemId'),
          status: generateAutocompleteItems(aData.status.statusItems, 'itemName', 'itemId'),
          responsible: aData.responsible,
        };
        setAutoCompleteOptions(acOptions);
      })
      .finally(() => {});
  };

  const validationSchema = Yup.object().shape({
    lineId: Yup.string().required(),
    startDate: Yup.string().required(),
    name: Yup.string().required(),
    duration: Yup.number().required(),
    responsiblePersonId: Yup.string().nullable(),
    responsibleUserGroupId: Yup.string().nullable(),
    responsibleCompanyId: Yup.string().nullable(),
    responsibleWorkerId: Yup.string().nullable(),
    responsibleType: Yup.string().required(),
    userGroupId: Yup.string().nullable(),
    typeId: Yup.string().nullable(),
    statusId: Yup.string().required(),
    description: Yup.string().nullable(),
    deadLine: Yup.string().nullable(),
    userId: Yup.string().nullable(),
    preparedness: Yup.number().nullable(),
    taskRatio: Yup.number().positive().nullable(),
    taskListRespPerson: Yup.array().of(
      Yup.object().shape({
        userId: Yup.string().required(),
      })
    ),
  });

  const valuesToForm = () => {
    const {
      id,
      name,
      lineId,
      startDate,
      duration,
      responsiblePersonId,
      responsibleUserGroupId,
      responsibleCompanyId,
      responsibleWorkerId,
      responsibleType,
      userGroupId,
      typeId,
      statusId,
      description,
      taskListAttachment,
      deadLine,
      userId,
      taskRatio,
      taskListRespPerson,
      preparedness,
    } = selectedValues;

    setInitValues({
      id,
      name,
      lineId,
      startDate,
      duration,
      responsiblePersonId,
      responsibleUserGroupId,
      responsibleCompanyId,
      responsibleWorkerId,
      responsibleType,
      userGroupId,
      typeId,
      statusId,
      description,
      taskListAttachment,
      deadLine,
      userId,
      taskRatio,
      preparedness: preparedness || '',
      projectOrderNumber: autocompleteData?.project?.find((item) => item.projectId === lineId)?.orderNumber,
      taskListRespPerson: taskListRespPerson?.map((respPerson) => ({
        id: respPerson.id,
        userId: respPerson.userId,
      })),
    });
  };

  const handleSubmit = (values) => {
    const isDialog = Boolean(dialog);
    const hasId = Boolean(values.id);

    if (isDialog) {
      if (hasId) updateObject(values);
      else handleInsertObject(values);
      return;
    }

    if (!editButtonVisible) {
      updateObject(values);
    } else if (!addField) {
      handleInsertObject(values);
    }
  };

  const updateObject = async (values) => {
    const updData = {
      ...values,
      updatedBy: user.userId,
      startDate: dayjs(values.startDate).format('YYYY-MM-DD') + 'T00:00:00.000Z',
      duration: parseInt(values.duration),
      preparedness: parseFloat(values.preparedness),
      taskRatio: parseFloat(values.taskRatio),
    };

    updData.existingFiles = updData.taskListAttachment?.map((f) => f.name);

    const formData = new FormData();
    formData.append('data', JSON.stringify(updData));

    if (updData.taskListAttachment) {
      for (const file of updData.taskListAttachment) {
        if (file instanceof File) {
          formData.append('files', file);
        }
      }
    }

    taskListService.updateTaskList(formData).then((data) => {
      addNotification({
        content: 'Sikeres módosítás!',
        type: NotificationType.SUCCESS,
      });

      setSelectedValues(data);
      setRefetchTable(true);
      setEditButtonvisible(true);
      setViewField(true);

      if (dialog) {
        const merged = {
          ...dialog.data,
          ...data,
        };
        dialog.setTaskChanged(true);
        dialog.handleClose();
        dialog.setFieldValue(`taskList[${dialog.data.index}]`, merged);
        dialog.setSelectedValues((prev) => {
          return {
            ...prev,
            taskLists: prev.taskLists.map((tl) => {
              if (tl.id === merged.id) {
                return merged;
              } else {
                return tl;
              }
            }),
          };
        });
      }
    });
  };

  const handleInsertObject = (values) => {
    const insertObject = {
      ...values,
      createdBy: user.userId,
      startDate: dayjs(values.startDate).format('YYYY-MM-DD') + 'T00:00:00.000Z',
      duration: parseInt(values.duration),
      taskRatio: parseFloat(values.taskRatio),
      preparedness: parseFloat(values.preparedness),
    };

    const formData = new FormData();
    formData.append('data', JSON.stringify(insertObject));

    if (insertObject.taskListAttachment) {
      for (const file of insertObject.taskListAttachment) {
        formData.append('files', file);
      }
    }

    taskListService
      .createTaskList(formData)
      .then((data) => {
        addNotification({
          content: 'Sikeres mentés!',
          type: NotificationType.SUCCESS,
        });
        setSelectedValues(data);
        setRowSelectionModel(data.id);
        setRefetchTable(true);
        setAddField(true);
        setViewField(true);

        if (dialog) {
          dialog.setTaskChanged(true);
          dialog.handleClose();
        }
      })
      .finally(() => {});
  };

  const handleRemoveElement = async () => {
    const deleteData = {
      deletedBy: user.userId,
    };
    taskListService.deleteTaskList(deleteData, selectedValues.id).then(() => {
      addNotification({
        content: 'Sikeres törlés!',
        type: NotificationType.WARNING,
      });
      setRefetchTable(true);
      setShowDeleteDialog(false);
      setSelectedValues({});
      setInitValues(def);
    });
  };

  return (
    <>
      <div className="h-full overflow-auto custom-scrollbar">
        <div className="flex items-center flex-wrap justify-between">
          {!dialog && (
            <FormHeaderUI
              addClick={() => {
                setSelectedValues(defaultSelectedValues);
                setViewField(false);
                setAddField(false);
              }}
              addDisabled={!canEdit}
              modClick={() => {
                setViewField(false);
                setEditButtonvisible(false);
              }}
              modDisabled={operatorsDisabled || !canEdit}
              deleteClick={() => {
                setShowDeleteDialog(true);
              }}
              deleteDisabled={operatorsDisabled || !canEdit}
              copyClick={() => {
                setViewField(false);
                setAddField(false);
              }}
              copyDisabled={operatorsDisabled || !canEdit}
            />
          )}
          {!(dialog && dialog.disabledField) && (
            <div>
              <History disabled={operatorsDisabled} onClick={() => setHistoryDialogOpen(!historyDialogOpen)} />
            </div>
          )}
        </div>
        <Formik
          initialValues={initValues}
          validationSchema={validationSchema}
          validateOnChange={false}
          onSubmit={handleSubmit}
          enableReinitialize
        >
          {({ resetForm }) => (
            <Form>
              <ProjectTaskFields
                viewField={!dialog ? viewField : dialog?.disabledField}
                autocompleteOptions={autocompleteOptions}
                autocompleteData={autocompleteData}
                selectedValues={selectedValues}
              />
              <ProjectTaskTabs
                viewField={!dialog ? viewField : dialog?.disabledField}
                selectedValues={selectedValues}
                defTaskListRespPerson={defTaskListRespPerson}
                autocompleteOptions={autocompleteOptions}
              />
              {!(dialog && dialog.disabledField) && (
                <FormButtonPanelUI
                  onCancelClick={() => {
                    if (!dialog) {
                      setViewField(true);
                      setEditButtonvisible(true);
                      setAddField(true);

                      resetForm();
                    } else {
                      dialog.handleClose();
                    }
                  }}
                  disabled={!dialog && editButtonVisible && addField}
                />
              )}
            </Form>
          )}
        </Formik>
      </div>

      <DeleteDialogUI
        handleRemoveElement={handleRemoveElement}
        show={showDeleteDialog}
        onHide={() => setShowDeleteDialog(false)}
      />

      <HistoryDialog
        open={historyDialogOpen}
        handleClose={() => {
          setHistoryDialogOpen(false);
        }}
        formId={projectTaskSubmenu}
        lineId={selectedValues.id}
        fieldConfig={[]}
      />
    </>
  );
};
